import React from 'react';
import { Form, Input, Button, Card, Breadcrumb, Skeleton, Select } from 'antd';
import { PageHeader } from 'components/mobile/components/PageHeader';
import { useTranslation } from 'react-i18next';

interface InviteUserProps {
  onInviteUser: (username: string, role: string, selectedTeams: string[]) => void;  
  team: {name: string, id: string}
  teamId: string | undefined;
  loading: boolean; 
  teamsToSelect: [];
}

/**
 * InviteUser is a functional component used to display a form for inviting a user.
 * @param {Function} props.onInviteUser - The method to handle inviting a user.
 * @param {{name: string, id: string, selectedValues: string[]}} props.team - The team to invite the user to.
 * @param {string | undefined} props.teamId - The id of the team to invite the user to.
 * @param {boolean} props.loading - The loading status of the component.
 * @param {[]} props.teamsToSelect - The list of teams to select from.
 * @returns {JSX.Element} The InviteUser component.
 */
export const InviteUser: React.FC<InviteUserProps> = ({
  onInviteUser,
  team,
  teamId,
  loading,
  teamsToSelect
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  /**
   * Method to handle the form submission
   * @param {object} values - Form values
   */
  const onFinish = (values: { username: string, role: string, selectTeam: string[] }) => {
    
    if(teamId != undefined)
      onInviteUser(values.username, values.role, [teamId]);
    else
      onInviteUser(values.username, values.role, values.selectTeam);
  };

  return (
    <div className="settings-notifications-container div-default white-bg padding-20">
      <PageHeader
        showBack={true}
        title={t('Invite User')}
        toPath={teamId == undefined ? "/organization/users" : "/organization/teams/" + teamId}
      />
      <br />
      {loading ? <Skeleton active />
        : (
          <div>
            <Breadcrumb>
              <Breadcrumb.Item>
                {teamId == undefined
                  ? null
                  : <a href="/organization/teams">{t('Teams')}</a>}
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {teamId == undefined
                  ? <a href="/organization/users">{t('Users')}</a>
                  : <a href={`/organization/teams/${teamId}`}>{team?.name}</a>}
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t('Invite User')}</Breadcrumb.Item>
            </Breadcrumb>
            <Card title={t('Invite User')} bordered={false}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{ }}
              >
                <Form.Item
                  label={t('Role')}
                  name="role"
                  rules={[{ required: true, message: t('Please input a role!') }]}
                >
                  <Select getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                    <Select.Option value={t('Team Manager')}>{t('Team Manager')}</Select.Option>
                    <Select.Option value={t('Team Member')}>{t('Team Member')}</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t('Select Team')}
                  name="selectTeam"
                  rules={[{ required: teamId == undefined, message: t('Please select at least one option!') }]}
                >
                  <Select
                    mode="multiple"
                    placeholder={t('Select Team')}
                    allowClear
                    disabled={teamId != undefined}
                    defaultValue={teamId != undefined ? [team?.id] : []}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  >
                    {teamsToSelect?.map((team: any) => (
                      <Select.Option key={team.id} value={team.id}>
                        {team.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t('Email')}
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: t('Please input your email!')
                    },
                    {
                      type: 'string',
                      message: t('Please enter a username or email!')
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  {t('Invite User')}
                </Button>
              </Form>
            </Card>
          </div>
        )}
    </div>
  );
};
